import { useState } from 'react';
import { Gavel, Clock, TrendingUp, Trophy, ChevronDown, ChevronUp } from 'lucide-react';
import { formatPrice, timeUntil } from '@/utils';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/store';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

interface Bid {
  id: string;
  bidder_id: string;
  bidder_username: string;
  amount: number;
  is_winning: boolean;
  is_auto_bid: boolean;
  created_at: string;
}

interface AuctionBidSectionProps {
  listingId: string;
  currentBid: number;
  startingPrice: number;
  endsAt: string;
  bids: Bid[];
  onBidPlaced: () => void;
}

export default function AuctionBidSection({
  listingId, currentBid, startingPrice, endsAt, bids, onBidPlaced
}: AuctionBidSectionProps) {
  const { user, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const [bidAmount, setBidAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [useAutoBid, setUseAutoBid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBidHistory, setShowBidHistory] = useState(false);

  const minBid = (currentBid ?? startingPrice ?? 0) + 1;
  const isEnded = endsAt && new Date(endsAt) < new Date();
  const winningBid = bids.find(b => b.is_winning);
  const userIsWinning = winningBid?.bidder_id === user?.id;
  const userHasBid = bids.some(b => b.bidder_id === user?.id);

  const handleBid = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    if (!bidAmount || Number(bidAmount) < minBid) {
      toast.error(`Bid must be at least ${formatPrice(minBid)}`);
      return;
    }
    if (useAutoBid && (!maxAmount || Number(maxAmount) < Number(bidAmount))) {
      toast.error('Max bid must be greater than or equal to your bid');
      return;
    }

    setIsSubmitting(true);
    try {
      await apiClient.post('/bids', {
        listing_id: listingId,
        amount: Number(bidAmount),
        max_amount: useAutoBid ? Number(maxAmount) : undefined,
      });
      toast.success('Bid placed successfully!');
      setBidAmount('');
      setMaxAmount('');
      onBidPlaced();
    } catch (error: any) {
      toast.error(error.response?.data?.message ?? 'Failed to place bid');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isEnded) {
    return (
      <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 p-5">
        <div className="flex items-center gap-2 text-gray-500 dark:text-zinc-500 mb-3">
          <Clock className="h-5 w-5" />
          <span className="font-semibold">Auction Ended</span>
        </div>
        {winningBid ? (
          <div className="flex items-center gap-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 rounded-xl p-4">
            <Trophy className="h-6 w-6 text-yellow-500" />
            <div>
              <p className="font-bold text-yellow-800 dark:text-yellow-200">Won by {winningBid.bidder_username}</p>
              <p className="text-yellow-700 dark:text-yellow-300 text-sm">Winning bid: {formatPrice(winningBid.amount)}</p>
            </div>
          </div>
        ) : (
          <p className="text-gray-500 dark:text-zinc-500 text-sm">No bids were placed on this auction.</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Current bid display */}
      <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-5">
        {bids.length > 0 && (
          <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 rounded-xl px-4 py-3 mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-orange-500" />
              <span className="text-sm font-semibold text-orange-700 dark:text-orange-300">Top Bid</span>
              {winningBid && <span className="text-xs text-orange-500">by {winningBid.bidder_username}</span>}
            </div>
            <span className="text-xl font-bold text-orange-600">{formatPrice(currentBid)}</span>
          </div>
        )}
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm text-gray-500 dark:text-zinc-500">Starting Bid</p>
            <p className="text-2xl font-bold text-gray-700 dark:text-zinc-300">{formatPrice(startingPrice)}</p>
            <p className="text-xs text-gray-500 dark:text-zinc-500 mt-0.5">{bids.length} bid{bids.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500 dark:text-zinc-500 mb-1">Time remaining</p>
            <div className="flex items-center gap-1 text-orange-500 font-semibold">
              <Clock className="h-4 w-4" />
              <span>{timeUntil(endsAt)}</span>
            </div>
          </div>
        </div>

        {/* User status */}
        {userHasBid && (
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm mb-4 ${
            userIsWinning ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
          }`}>
            {userIsWinning ? (
              <><Trophy className="h-4 w-4" /> You're winning this auction!</>
            ) : (
              <><TrendingUp className="h-4 w-4" /> You've been outbid — place a higher bid</>
            )}
          </div>
        )}

        {/* Bid input */}
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-500 dark:text-zinc-500 mb-1 block">Your bid (min {formatPrice(minBid)})</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-zinc-500 font-medium">$</span>
              <input
                type="number"
                value={bidAmount}
                onChange={(e) => setBidAmount(e.target.value)}
                placeholder={minBid.toString()}
                min={minBid}
                step="1"
                className="w-full pl-7 pr-3 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>
          </div>

          {/* Auto-bid toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <div
              onClick={() => setUseAutoBid(!useAutoBid)}
              className={`w-10 h-5 rounded-full transition-colors relative ${useAutoBid ? 'bg-orange-500' : 'bg-gray-300'}`}
            >
              <div className={`absolute top-0.5 h-4 w-4 rounded-full bg-white dark:bg-zinc-900 shadow transition-transform ${useAutoBid ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </div>
            <span className="text-sm text-gray-600 dark:text-zinc-400">Enable auto-bidding</span>
          </label>

          {useAutoBid && (
            <div>
              <label className="text-xs text-gray-500 dark:text-zinc-500 mb-1 block">Maximum auto-bid amount</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-zinc-500 font-medium">$</span>
                <input
                  type="number"
                  value={maxAmount}
                  onChange={(e) => setMaxAmount(e.target.value)}
                  placeholder="Max you're willing to pay"
                  min={bidAmount || minBid}
                  step="1"
                  className="w-full pl-7 pr-3 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-zinc-500 mt-1">We'll automatically bid for you up to this amount</p>
            </div>
          )}

          <button
            onClick={handleBid}
            disabled={isSubmitting || !bidAmount}
            className="w-full py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
          >
            <Gavel className="h-4 w-4" />
            {isSubmitting ? 'Placing Bid...' : 'Place Bid'}
          </button>
        </div>
      </div>

      {/* Bid history */}
      {bids.length > 0 && (
        <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm overflow-hidden">
          <button
            onClick={() => setShowBidHistory(!showBidHistory)}
            className="w-full flex items-center justify-between px-5 py-3 text-sm font-semibold text-gray-700 dark:text-zinc-300 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900"
          >
            <span>Bid History ({bids.length})</span>
            {showBidHistory ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {showBidHistory && (
            <div className="divide-y divide-gray-200 dark:divide-zinc-800">
              {bids.map((bid, i) => (
                <div key={bid.id} className="flex items-center justify-between px-5 py-3">
                  <div className="flex items-center gap-2">
                    {i === 0 && <Trophy className="h-4 w-4 text-yellow-500" />}
                    <span className="text-sm font-medium">{bid.bidder_username}</span>
                    {bid.is_auto_bid && <span className="text-xs bg-gray-100 dark:bg-zinc-800 text-gray-500 dark:text-zinc-500 px-1.5 py-0.5 rounded">Auto</span>}
                  </div>
                  <span className={`text-sm font-bold ${i === 0 ? 'text-orange-600' : 'text-gray-500 dark:text-zinc-500'}`}>
                    {formatPrice(bid.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}